export interface ModelOption {
  id: string;
  name: string;
  tag?: string;
  description?: string;
}

export interface RetentionOption {
  value: number;
  label: string;
}

export interface PromptTemplateOption {
  id: string;
  name: string;
  description: string;
  prompt: string;
}

export const GROQ_ASR_MODELS: ModelOption[] = [
  {
    id: "whisper-large-v3-turbo",
    name: "Whisper Large v3 Turbo",
    tag: "Default · Recommended",
    description: "Fastest transcription, low latency, $0.04 / hour",
  },
  {
    id: "whisper-large-v3",
    name: "Whisper Large v3",
    tag: "High Accuracy",
    description: "Maximum accuracy across 99+ languages, $0.111 / hour",
  },
];

export const OPENROUTER_SUMMARY_MODELS: ModelOption[] = [
  {
    id: "google/gemini-2.5-flash",
    name: "Google Gemini 2.5 Flash",
    tag: "Recommended",
    description: "Fast, high quality, and highly economical",
  },
  {
    id: "google/gemini-2.5-pro",
    name: "Google Gemini 2.5 Pro",
    tag: "Deep Reasoning",
    description: "Superior synthesis for complex and dense lectures",
  },
  {
    id: "anthropic/claude-3.7-sonnet",
    name: "Claude 3.7 Sonnet",
    tag: "Frontier",
    description: "Hybrid reasoning and elite technical synthesis",
  },
  {
    id: "anthropic/claude-3.5-sonnet",
    name: "Claude 3.5 Sonnet",
    tag: "High Quality",
    description: "Exceptional structure, precision, and markdown formatting",
  },
  {
    id: "anthropic/claude-3.5-haiku",
    name: "Claude 3.5 Haiku",
    tag: "Ultra Fast",
    description: "Lightning speed and lowest token cost",
  },
  {
    id: "openai/gpt-4o",
    name: "OpenAI GPT-4o",
    tag: "Flagship",
    description: "Reliable general-purpose intelligence",
  },
  {
    id: "openai/gpt-4o-mini",
    name: "OpenAI GPT-4o Mini",
    tag: "Lightweight",
    description: "High speed, low cost baseline",
  },
  {
    id: "deepseek/deepseek-chat",
    name: "DeepSeek V3",
    tag: "High Value",
    description: "State-of-the-art open architecture reasoning",
  },
  {
    id: "meta-llama/llama-3.3-70b-instruct",
    name: "Meta Llama 3.3 70B",
    tag: "Open Weights",
    description: "Powerful open-source foundation model",
  },
];

export const RETENTION_PRESETS: RetentionOption[] = [
  { value: 7, label: "7 days (1 week)" },
  { value: 14, label: "14 days (2 weeks)" },
  { value: 30, label: "30 days (Default · 1 month)" },
  { value: 60, label: "60 days (2 months)" },
  { value: 90, label: "90 days (1 quarter)" },
  { value: 180, label: "180 days (~6 months)" },
  { value: 365, label: "365 days (1 year)" },
];

export const DEFAULT_PROMPT_TEXT = `You are summarizing a university lecture transcript into study notes.

Write GitHub-flavored Markdown using exactly this structure:

# {Subject} - {Date}

## TL;DR
Three to five sentences.

## Key concepts
- **Term** - definition as given in the lecture

## Detailed notes
### {Section derived from topic shifts}
...

## Formulas / code
Fenced blocks, preserved verbatim where dictated.

## Action items
- [ ] Assignments, deadlines, reading

## Open questions
Things the lecturer flagged as unresolved or "we'll cover next time".

Rules:
- Use only what is in the transcript. Do not invent facts.
- Transcription errors are expected; silently correct obvious mis-hearings of
  technical terms, and mark anything you cannot recover as [inaudible].
- Keep the lecturer's own definitions and wording for terms.
- Omit a section entirely if the lecture contains nothing for it.`;

export const PROMPT_TEMPLATES: PromptTemplateOption[] = [
  {
    id: "default",
    name: "Academic Lecture Notes (Default)",
    description: "Comprehensive notes with key concepts, detailed sections, formulas, and action items",
    prompt: DEFAULT_PROMPT_TEXT,
  },
  {
    id: "concise",
    name: "Concise / Executive Summary",
    description: "Quick 2-minute review with high-level takeaways and core definitions",
    prompt: `You are summarizing a university lecture transcript into a concise executive briefing for quick review.

Write GitHub-flavored Markdown using exactly this structure:

# {Subject} - {Date}

## Core Takeaways
- Bulleted list of the 3 to 5 most critical points covered today.

## High-Level Summary
One to two clear, high-density paragraphs explaining the main topic, thesis, and conclusions.

## Key Terminology
- **Term** - concise 1-sentence definition based on the lecturer's explanation.

## Action Items & Deadlines
- [ ] Any assigned homework, readings, or upcoming exam dates mentioned.

Rules:
- Be concise and direct. Prioritize clarity and brevity over exhaustive depth.
- Use only what is in the transcript. Do not invent facts.
- Silently correct obvious mis-hearings of technical terms.`,
  },
  {
    id: "exam_prep",
    name: "Exam Preparation & Key Concepts",
    description: "Targeted for test revision with model questions, traps, and definitions",
    prompt: `You are summarizing a university lecture transcript into an intensive exam preparation study guide.

Write GitHub-flavored Markdown using exactly this structure:

# {Subject} - {Date} (Exam Prep Guide)

## Essential Concepts & Definitions (Must-Know)
- **Concept / Term**: Clear explanation, why it matters, and how the professor emphasized it.

## Key Formulas, Theorems & Code
Preserve formulas or code verbatim with explanations of each variable and parameter.

## Probable Exam Questions & Model Answers
### Q1: [Anticipated question based on professor's emphasis]
- **Answer key**: Core points required for full credit.

### Q2: [Anticipated question]
- **Answer key**: Core points.

## Common Pitfalls & Edge Cases
Things the professor warned against or highlighted as common student mistakes.

## Quick Self-Test Checklist
- [ ] Can explain concept A without notes
- [ ] Can derive or apply formula B

Rules:
- Highlight items the lecturer specifically stressed or flagged for exams.
- Strict factual adherence to the transcript.`,
  },
  {
    id: "technical",
    name: "Detailed Technical & Code Notes",
    description: "Engineering and STEM focused, highlighting code blocks, algorithms, and proofs",
    prompt: `You are summarizing an advanced STEM/computer science/engineering lecture into comprehensive technical documentation.

Write GitHub-flavored Markdown using exactly this structure:

# {Subject} - {Date}

## Technical Overview
A rigorous architectural or theoretical summary of the systems, algorithms, or theories presented.

## Core Principles & Formulations
- Mathematical equations or data structures in LaTeX/markdown code blocks.
- Formal definitions of terms.

## Detailed Architecture & Implementation
### 1. [Sub-system or Proof Stage]
Detailed explanation with code snippets, pseudo-code, or step-by-step logic.

### 2. [Next Component]
...

## Trade-offs & Complexity Analysis
- Complexity (time/space or performance trade-offs discussed by lecturer).
- Alternative approaches compared during class.

## Practical Exercises & Code Tasks
- [ ] Implementation tasks, readings, or problem sets assigned.

Rules:
- Preserve code, math, and technical precision verbatim.
- Mark inaudible code or syntax as [inaudible] rather than guessing.`,
  },
];

/**
 * Returns a sorted list of IANA timezones supported by the browser.
 */
export function getAvailableTimezones(): string[] {
  try {
    if (typeof Intl !== "undefined" && typeof (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf === "function") {
      return (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf("timeZone");
    }
  } catch {
    // fallback
  }

  return [
    "Africa/Cairo",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/New_York",
    "America/Sao_Paulo",
    "America/Toronto",
    "America/Vancouver",
    "Asia/Bangkok",
    "Asia/Dubai",
    "Asia/Hong_Kong",
    "Asia/Jerusalem",
    "Asia/Seoul",
    "Asia/Shanghai",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Melbourne",
    "Australia/Sydney",
    "Europe/Amsterdam",
    "Europe/Berlin",
    "Europe/Dublin",
    "Europe/Kyiv",
    "Europe/London",
    "Europe/Madrid",
    "Europe/Paris",
    "Europe/Prague",
    "Europe/Rome",
    "Europe/Warsaw",
    "Europe/Zurich",
    "Pacific/Auckland",
    "UTC",
  ];
}

export function getUserDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Kyiv";
  } catch {
    return "Europe/Kyiv";
  }
}
